import { MouseEvent, useEffect, useState } from 'react';

import { Box, Button, Grid, Typography } from '@mui/material';

import { useGetDatesQuery } from 'state/apiSlice';
import { useAppSelector } from 'state/hooks';
import { selectProject, selectProjectSpecialDates } from 'state/workflowSlice';

import { SpecialDateRequest } from 'appTypes';
import { formatDateShort } from 'features/user_admin/common';

import FontAwesomeIcon from '../FontAwesomeIcon';
import { classNames } from '../utils';
import { MAX_RANGE_DAYS } from './MultiDayRangePicker';
import { areDatesConsecutive } from './utils';

import commonStyles from './DateTimeSelector.module.css';
import styles from './SpecialDatesPicker.module.css';

interface ISpecialEventsDateList {
  targetDate?: string;
  endDate?: string;
  onSelect: (date: string, endDate?: string) => void;
  forbiddenDates?: string[];
  columns?: number;
  open?: boolean;
  allowRange?: boolean;
}

export function SpecialEventsDateList({
  targetDate = '',
  endDate,
  onSelect,
  forbiddenDates,
  columns = 2,
  open = true,
  allowRange = false,
}: ISpecialEventsDateList) {
  const isUnavailable = useDateUnavailable(forbiddenDates);
  const [rangeStart, setRangeStart] = useState<{
    eventKey: string;
    date: string;
  } | null>(null);

  useEffect(() => {
    if (!open) setRangeStart(null);
  }, [open]);

  const isSelected = (sdr: SpecialDateRequest, date: string) => {
    // while picking a shift range, only show the new selection
    if (rangeStart) {
      return rangeStart.eventKey === sdr.hash_id && rangeStart.date === date;
    }
    if (!targetDate) return false;
    const rangeEnd = endDate || targetDate;
    return date >= targetDate && date <= rangeEnd;
  };

  const isDisabled = (date: string) => {
    return isUnavailable(date);
  };

  const handleClick = (
    sdr: SpecialDateRequest,
    date: string,
    e: MouseEvent,
  ) => {
    // if range is not allowed or shift key is not pressed, set the current special date and select the date
    if (!allowRange || !e.shiftKey) {
      setRangeStart(null);
      onSelect(date);
      return;
    }

    // set the range start on the first click on a new event
    const key = sdr.hash_id;
    if (!rangeStart || rangeStart.eventKey !== key) {
      setRangeStart({ eventKey: key, date });
      return;
    }
    // second click sets the end date
    const [start, end] =
      rangeStart.date <= date
        ? [rangeStart.date, date]
        : [date, rangeStart.date];
    // filter the dates to be available and in range
    const dates = sdr.dates
      .filter((d) => d >= start && d <= end && !isUnavailable(d))
      .sort();

    if (dates.length > MAX_RANGE_DAYS || !areDatesConsecutive(dates)) {
      setRangeStart({ eventKey: key, date });
      return;
    }

    setRangeStart(null);
    onSelect(dates[0], dates.length > 1 ? dates[dates.length - 1] : undefined);
  };

  return (
    <SpecialDatesList
      columns={columns}
      isSelected={isSelected}
      isDisabled={isDisabled}
      onClick={handleClick}
    />
  );
}

interface ISpecialEventsMultiDateList {
  selectedDates: Set<string>;
  onToggleDate: (date: string, sdr: SpecialDateRequest) => void;
  forbiddenDates?: string[];
  maxSelection?: number;
  columns?: number;
}

export function SpecialEventsMultiDateList({
  selectedDates,
  onToggleDate,
  forbiddenDates,
  maxSelection,
  columns = 2,
}: ISpecialEventsMultiDateList) {
  const isUnavailable = useDateUnavailable(forbiddenDates);

  const isDisabled = (date: string) => {
    if (isUnavailable(date)) return true;
    if (selectedDates.has(date)) return false;
    return maxSelection !== undefined && selectedDates.size >= maxSelection;
  };

  return (
    <SpecialDatesList
      columns={columns}
      isSelected={(_sdr, date) => selectedDates.has(date)}
      isDisabled={(date) => isDisabled(date)}
      onClick={(sdr, date) => onToggleDate(date, sdr)}
    />
  );
}

function useDateUnavailable(forbiddenDates?: string[]) {
  const project = useAppSelector(selectProject);
  const { data: datesInfo = [] } = useGetDatesQuery(project);
  return (date: string) =>
    Boolean(forbiddenDates?.includes(date)) ||
    !datesInfo.some((el) => el.date === date);
}

interface ISpecialDatesList {
  columns: number;
  isSelected: (sdr: SpecialDateRequest, date: string) => boolean;
  isDisabled: (date: string) => boolean;
  onClick: (sdr: SpecialDateRequest, date: string, e: MouseEvent) => void;
}

function SpecialDatesList({
  columns,
  isSelected,
  isDisabled,
  onClick,
}: ISpecialDatesList) {
  const specialDates = useAppSelector(selectProjectSpecialDates);
  const span = 12 / columns;

  return (
    <Box className={styles.dateList}>
      {specialDates.map((sdr) => (
        <Box key={sdr.name} className={styles.card}>
          <Typography variant="body2" fontSize={'0.75rem'} mb={1}>
            {sdr.name}
          </Typography>
          <Grid container spacing={0.5}>
            {sdr.dates.map((date) => (
              <Grid item xs={span} key={date}>
                <SpecialDateTile
                  date={date}
                  selected={isSelected(sdr, date)}
                  disabled={isDisabled(date)}
                  onClick={(e) => onClick(sdr, date, e)}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      ))}
    </Box>
  );
}

interface ISpecialDateTile {
  date: string;
  selected: boolean;
  disabled: boolean;
  onClick: (e: MouseEvent) => void;
}

function SpecialDateTile({
  date,
  selected,
  disabled,
  onClick,
}: ISpecialDateTile) {
  return (
    <Button
      startIcon={
        selected ? (
          <FontAwesomeIcon
            type="sharp-solid"
            name="square-check"
            sx={{ color: 'rgba(32, 32, 32, 0.80)' }}
          />
        ) : undefined
      }
      onClick={onClick}
      id={selected ? commonStyles.button_active : undefined}
      disabled={disabled}
      value={date}
      fullWidth
      className={classNames(commonStyles.defaultBtn, styles.dateBtn)}
    >
      <Typography variant="caption">{formatDateShort(date)}</Typography>
    </Button>
  );
}
